import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, Home, TrendingUp, Cpu, ShieldCheck, FileCheck2, Info } from "lucide-react";
import { Reveal, SectionHeading, Eyebrow } from "../components/common/Section";
import PageHero from "../components/common/PageHero";
import { Button } from "../components/ui/button";

const STORIES = [
  {
    icon: Home,
    sector: "Mortgage lender",
    title: "Catching potential vulnerability before it becomes a poor outcome.",
    challenge: "A lender's QA team could only manually review a small sample of broker and customer calls, and worried about missing signs a customer needed extra support — or conversations drifting toward advice.",
    steps: ["Lögberg screens every conversation against the firm's risk indicators", "Potential vulnerability and advice-boundary language is surfaced and prioritised", "Material cases route to a human reviewer with the context attached", "The reviewer confirms, dismisses or escalates — and it's recorded"],
    evidence: ["Interaction & timestamp", "Risk indicator & policy", "Reviewer decision", "Final outcome"],
    outcome: "The QA team moves from sampling a fraction of calls to a risk-led queue — and can show, per case, what was flagged, who decided and why.",
  },
  {
    icon: TrendingUp,
    sector: "Wealth manager",
    title: "Letting staff use AI without leaking client information.",
    challenge: "Advisers wanted to use generative AI to draft and summarise, but the firm couldn't risk client data being pasted into consumer tools or unsuitable content going out.",
    steps: ["Aegis inspects prompts and uploads before they reach the model", "Designated sensitive client information is detected and redacted or blocked", "Outputs are checked before release; material cases escalate to a human", "Every intervention is recorded"],
    evidence: ["Event & policy applied", "Action (redact / block / escalate)", "Reviewer & decision", "Minimal evidence reference"],
    outcome: "Advisers get the productivity of AI while the firm keeps client information inside policy — with a defensible record of what was allowed and what wasn't.",
  },
  {
    icon: Cpu,
    sector: "Defence supplier",
    title: "Keeping a human in the loop as AI starts to take actions.",
    challenge: "An AI agent was moving from answering questions to proposing actions — accessing internal systems and sending communications — and the organisation needed control before anything executed.",
    steps: ["norse3 evaluates each proposed action against an allow-list and policy", "Low-risk actions continue; high-impact actions are held", "An authorised person approves or rejects out-of-scope actions", "The decision and outcome are evidenced"],
    evidence: ["Proposed action & risk", "Policy applied", "Human authorisation", "Approve / reject + outcome"],
    outcome: "The organisation adopts agentic AI with a clear boundary: designated actions can't execute without the right policy — and, where needed, the right person.",
  },
];

export default function CaseStudies() {
  useEffect(() => { document.title = "Case Studies | norse3"; }, []);
  return (
    <div>
      <PageHero
        eyebrow="Case Studies"
        title="What controlled AI adoption looks like."
        subtitle="Illustrative scenarios showing how norse3 sits around AI and customer conversations — spotting risk, applying policy, keeping humans accountable and creating evidence."
      />

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="mb-8 flex items-start gap-2.5 rounded-xl border border-signal-amber/30 bg-signal-amber/[0.05] px-4 py-3 text-sm text-signal-amber/90">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            These are illustrative scenarios using synthetic data. They describe how norse3 can be configured — not named clients or guaranteed outcomes.
          </div>

          <div className="space-y-6">
            {STORIES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={i * 60}>
                  <article className="card-rise rounded-lg border border-white/10 bg-navy-900/60 p-6 sm:p-8 hover:border-primary/40">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                      <span className="rounded-full border border-white/12 bg-white/[0.03] px-3 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{s.sector}</span>
                    </div>
                    <h2 className="mt-4 text-2xl font-bold text-foreground text-balance">{s.title}</h2>
                    <div className="mt-6 grid lg:grid-cols-2 gap-6">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/70">The challenge</p>
                        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.challenge}</p>
                        <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground/70">What norse3 did</p>
                        <div className="mt-2.5 space-y-1.5">
                          {s.steps.map((st, j) => (
                            <div key={j}>
                              <div className="rounded-lg border border-white/10 bg-navy-800/50 px-3.5 py-2.5 text-sm text-foreground/90">{st}</div>
                              {j < s.steps.length - 1 && <div className="flex justify-center py-0.5"><ArrowDown className="h-3.5 w-3.5 text-muted-foreground/40" /></div>}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-primary">Evidence produced</p>
                        <div className="mt-2.5 grid gap-2">
                          {s.evidence.map((e) => (
                            <div key={e} className="flex items-center gap-2.5 rounded-lg border border-primary/25 bg-primary/[0.05] px-3.5 py-2.5 text-sm text-foreground/90"><FileCheck2 className="h-4 w-4 text-primary" /> {e}</div>
                          ))}
                        </div>
                        <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-signal-green">Outcome</p>
                        <p className="mt-2 rounded-lg border border-signal-green/25 bg-signal-green/[0.05] px-4 py-3 text-sm text-foreground/90 leading-relaxed">{s.outcome}</p>
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>

          <div className="mt-12 rounded-lg border border-white/10 bg-gradient-to-b from-navy-800/60 to-navy-900/60 p-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">See it against your own environment.</h2>
            <p className="mt-3 text-muted-foreground">We'll walk a realistic scenario from your world and show the evidence it produces.</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90"><Link to="/book-a-demo" data-testid="casestudy-cta">Book a walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link></Button>
              <Button asChild size="lg" variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground"><Link to="/governance-tools">Assess your AI governance</Link></Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
